import { useMutation, useQuery, useQueryClient } from 'react-query';

import { getCompareSets } from 'api/compare-sets';
import { getDataImportGroups } from 'api/data-import-groups';
import * as dataViews from 'api/data-views';
import { getDataViews } from 'api/data-views';
import * as geoDatasets from 'api/geodatasets';
import { getGeoDatasets } from 'api/geodatasets';
import * as mimicBoards from 'api/mimic-boards';
import { getMimicBoards } from 'api/mimic-boards';
import { getPipelineDefinitions } from 'api/pipelines';
import * as reports from 'api/reports';
import { getReports } from 'api/reports';
import {
  ApplicationId,
  DataImportGroup,
  DataView,
  GeoDataset,
  MimicBoard,
  PipelineDefinitionPreview,
  Report,
  Resource,
  ResourceEntity,
  ResourceType,
  UpdateDataViewsPartialPayload,
  UpdateGeoDatasetsPartialPayload,
  UpdateMimicBoardsPartialPayload,
  UpdateReportsPartialPayload,
} from 'interfaces';
import { CompareSet } from 'interfaces/compare-set';

export function useResourcesQuery(applicationIds: ApplicationId[]) {
  // TODO: use `useQueries` when it supports Suspense: https://github.com/tannerlinsley/react-query/pull/2109
  const { data } = useQuery(['resources', { applicationIds }], async () => {
    let allDataViews: DataView[] = [];
    let allMimicBoards: MimicBoard[] = [];
    let allReports: Report[] = [];
    let allMaps: GeoDataset[] = [];
    let allPipelines: PipelineDefinitionPreview[] = [];
    let allCompareSets: CompareSet[] = [];
    let allImportGroups: DataImportGroup[] = [];

    for (var applicationId of applicationIds) {
      const [dataViews, mimicBoards, reports, maps, pipelines, compareSets, dataImportGroups] =
        await Promise.all([
          getDataViews(applicationId),
          getMimicBoards(applicationId),
          getReports(applicationId),
          getGeoDatasets(applicationId),
          getPipelineDefinitions(applicationId),
          getCompareSets(applicationId),
          getDataImportGroups(applicationId),
        ]);

      allDataViews = [...allDataViews, ...dataViews];
      allMimicBoards = [...allMimicBoards, ...mimicBoards];
      allReports = [...allReports, ...reports];
      allMaps = [...allMaps, ...maps];
      allPipelines = [...allPipelines, ...pipelines];
      allCompareSets = [...allCompareSets, ...compareSets];
      allImportGroups = [...allImportGroups, ...dataImportGroups];
    }

    return Object.fromEntries<ResourceEntity[]>([
      ['DataView', allDataViews],
      ['Report', allReports],
      ['MimicBoard', allMimicBoards],
      ['GeoDataset', allMaps],
      ['PipelineDefinition', allPipelines],
      ['CompareSet', allCompareSets],
      ['DataImportGroup', allImportGroups],
    ]);
  });

  const resourceTypes = Object.keys(data!) as ResourceType[];
  const resources = resourceTypes.reduce<Resource[]>((acc, type) => {
    return [
      ...acc,
      ...data![type].map((x) => ({
        type,
        resourceId: x.id,
        name: x.name,
        label: x.label,
        iconUri: x.iconUri,
        categoryId: x.categoryId,
        applicationId: x.applicationId,
        sortOrder: x.sortOrder,
      })),
    ];
  }, []);

  return {
    resourceTypes,
    resources,
  };
}

export interface BatchResourceUpdatePayload {
  dataViews?: UpdateDataViewsPartialPayload;
  reports?: UpdateReportsPartialPayload;
  mimicBoards?: UpdateMimicBoardsPartialPayload;
  geoDatasets?: UpdateGeoDatasetsPartialPayload;
}

export function useBatchResourceUpdateMutation(applicationIds: ApplicationId[]) {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: BatchResourceUpdatePayload) => {
      const promises: Promise<any>[] = [];

      for (var applicationId of applicationIds) {
        if (payload.dataViews) {
          promises.push(dataViews.updateDataViewsPartial(applicationId, payload.dataViews));
        }
        if (payload.reports) {
          promises.push(reports.updateReportsPartial(applicationId, payload.reports));
        }
        if (payload.mimicBoards) {
          promises.push(mimicBoards.updateMimicBoardsPartial(applicationId, payload.mimicBoards));
        }
        if (payload.geoDatasets) {
          promises.push(geoDatasets.updateGeoDatasetsPartial(applicationId, payload.geoDatasets));
        }
      }

      return Promise.all(promises);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['resources', { applicationIds }]);
      },
    }
  );
}
